import { useMarketplaceLabel, marketplaceExportFields } from "../configurazione/marketplaceDisplay";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import DataTable from "../../interfaccia/DataTableBase";
import umanizzaDataSenzaOre from "../../interfaccia/funzioniComuni/umanizzaDataSenzaOre";

function padTo2Digits(num) {
  return num.toString().padStart(2, "0");
}

function formatDateInput(date = new Date()) {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join("-");
}

function normalizeDateForCsv(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

function formatDateForTable(value) {
  if (!value) return "";
  try {
    return umanizzaDataSenzaOre(value);
  } catch (err) {
    return normalizeDateForCsv(value);
  }
}

function formatEuro(value) {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (Number.isNaN(number)) return "";
  return number.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (
    stringValue.includes(";") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function formatArticoliForCsv(articoliArr = []) {
  if (!Array.isArray(articoliArr) || articoliArr.length === 0) return "";
  return articoliArr
    .map((art, idx) => {
      const importo = formatEuro(art.importoUnitario);
      return [
        `${idx + 1})`,
        art.codiceVsArticolo || "N/D",
        art.descrizioneArticolo || "N/D",
        `qta ${art.quantita ?? 0}`,
        importo ? `${importo} EUR` : "",
      ]
        .filter(Boolean)
        .join(" | ");
    })
    .join("\r\n");
}

function formatNoteForCsv(noteArr = []) {
  if (!Array.isArray(noteArr) || noteArr.length === 0) return "";
  return noteArr
    .map((nota, idx) => {
      return [
        `${idx + 1})`,
        formatDateForTable(nota.dataNota) || "",
        nota.contenutoNota || "",
      ]
        .filter(Boolean)
        .join(" | ");
    })
    .join("\r\n");
}

function downloadCsvFromRows(rows, fileName) {
  const delimiter = ";";
  const headers = Object.keys(rows[0] || {});
  const lines = [headers.join(delimiter)];

  rows.forEach((row) => {
    const line = headers
      .map((header) => escapeCsvValue(row[header]))
      .join(delimiter);
    lines.push(line);
  });

  const csv = `\uFEFF${lines.join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function renderArticoli(articoliArr = []) {
  if (!Array.isArray(articoliArr) || articoliArr.length === 0) return "-";
  return (
    <div className="pv1">
      {articoliArr.map((art, idx) => (
        <div key={`${art.codiceVsArticolo || "art"}-${idx}`} className="mb1">
          <span className="b">{art.codiceVsArticolo || "N/D"}</span>
          {" - "}
          {art.descrizioneArticolo || "N/D"}
          {" - "}
          qta {art.quantita ?? 0}
        </div>
      ))}
    </div>
  );
}

function renderNote(noteArr = []) {
  if (!Array.isArray(noteArr) || noteArr.length === 0) return "-";
  return (
    <div className="pv1">
      {noteArr.map((nota, idx) => (
        <div key={`${nota.dataNota || "nota"}-${idx}`} className="mb1">
          <span className="b">{formatDateForTable(nota.dataNota)}</span>
          {" - "}
          {nota.contenutoNota || ""}
        </div>
      ))}
    </div>
  );
}

function StatDatiGraneCorrieri({ token }) {
  const marketplaceLabel = useMarketplaceLabel();
  const today = new Date();
  const startLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  const [input, setInput] = useState({
    dataStart: formatDateInput(startLastMonth),
    dataEnd: formatDateInput(endLastMonth),
  });
  const [rangeApplicato, setRangeApplicato] = useState({
    dataStart: formatDateInput(startLastMonth),
    dataEnd: formatDateInput(endLastMonth),
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          dataStart: rangeApplicato.dataStart,
          dataEnd: rangeApplicato.dataEnd,
        });
        const response = await axios.get(
          `${apiUrl}/contab/get/datiGraneCorrieri?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setRows(response?.data?.data || []);
      } catch (err) {
        console.error("Errore caricamento dati grane corrieri", err);
        setError("Errore caricamento dati grane corrieri");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, rangeApplicato.dataStart, rangeApplicato.dataEnd]);

  const columns = useMemo(
    () => [
      {
        selector: (row) => row.tipoProblema,
        name: "Tipo problema",
        sortable: true,
        grow: 1.2,
      },
      {
        selector: (row) => row.dataProblema,
        name: "Data problema",
        sortable: true,
        width: "140px",
        format: (row) => formatDateForTable(row.dataProblema),
      },
      {
        selector: (row) => row.numeroOrdinePrestashop,
        name: "N. ordine prestashop",
        sortable: true,
        width: "170px",
      },
      {
        selector: (row) => row.rifOrdineMarketplace,
        name: "Rif. ordine marketplace",
        sortable: true,
        grow: 1.2,
      },
      {
        selector: (row) => row.corriere,
        name: "Corriere",
        sortable: true,
        width: "130px",
      },
      {
        selector: (row) => row.paeseSpedizione,
        name: "Paese spedizione",
        sortable: true,
        width: "140px",
      },
      {
        selector: (row) => row.tipoReso,
        name: "Tipo reso",
        sortable: false,
        grow: 1.1,
      },
      {
        selector: (row) => row.totaleRimborso,
        name: "Totale rimborso",
        sortable: true,
        width: "150px",
        format: (row) =>
          row.totaleRimborso || Number(row.totaleRimborso) === 0
            ? `${formatEuro(row.totaleRimborso)} €`
            : "",
      },
      {
        selector: (row) => row.articoliArr,
        name: "Articoli (array)",
        sortable: false,
        grow: 2,
        cell: (row) => renderArticoli(row.articoliArr),
      },
      {
        selector: (row) => row.noteArr,
        name: "Note (array)",
        sortable: false,
        grow: 2.3,
        cell: (row) => renderNote(row.noteArr),
      },
    ],
    [],
  );

  function handleChange(evt) {
    const { name, value } = evt.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  }

  function applicaRange() {
    setRangeApplicato({ ...input });
  }

  function esportaCsv() {
    if (!rows.length) return;

    const csvRows = rows.map((row) => ({
      "Tipo problema": row.tipoProblema || "",
      "Data problema": normalizeDateForCsv(row.dataProblema),
      "N. ordine prestashop": row.numeroOrdinePrestashop || "",
      "Rif. ordine marketplace": row.rifOrdineMarketplace || "",
      ...marketplaceExportFields(row.marketplace, marketplaceLabel),
      "Data ordine": normalizeDateForCsv(row.dataOrdine),
      "Data partenza": normalizeDateForCsv(row.dataPartenza),
      Corriere: row.corriere || "",
      "Totale importo ordine": row.totaleImportoOrdine ?? "",
      "Totale importo contrassegno": row.totaleImportoContrassegno ?? "",
      "Tipo ordine": row.tipoOrdine || "",
      "ID Spedizione Domino": row.idSpedizioneDomino || "",
      "Paese spedizione": row.paeseSpedizione || "",
      NomeCognome: row.nomeCognome || "",
      "C/O": row.careOf || "",
      Indirizzo: row.indirizzo || "",
      CAP: row.cap || "",
      Città: row.citta || "",
      Provincia: row.provincia || "",
      Tipo_reso: row.tipoReso || "",
      Totale_rimborso: row.totaleRimborso ?? "",
      "N. articoli": Array.isArray(row.articoliArr)
        ? row.articoliArr.length
        : 0,
      Articoli: formatArticoliForCsv(row.articoliArr),
      "N. note": Array.isArray(row.noteArr) ? row.noteArr.length : 0,
      Note: formatNoteForCsv(row.noteArr),
    }));

    const fileName = `grane-corrieri-${rangeApplicato.dataStart}_${rangeApplicato.dataEnd}.csv`;
    downloadCsvFromRows(csvRows, fileName);
  }

  return (
    <div className="sfondoBiancoTema pa3">
      <div className="w-100 flex flex-wrap justify-center items-end tc mb3">
        <div className="w-50 pa2 pb0 mr2 mw5">
          <label className="mr1 b" htmlFor="dataStart">
            Da data
          </label>
          <input
            type="date"
            name="dataStart"
            onChange={handleChange}
            value={input.dataStart}
            className="input-reset inputOrizzontale"
          />
        </div>
        <div className="w-50 pa2 pb0 mw5">
          <label className="mr1 b" htmlFor="dataEnd">
            a data
          </label>
          <input
            type="date"
            name="dataEnd"
            onChange={handleChange}
            value={input.dataEnd}
            className="input-reset inputOrizzontale"
          />
        </div>
        <button
          onClick={applicaRange}
          className="dim tastoInputOrizzontaleNoRadNoHeight br2 ml3 mr3"
        >
          {stilePulsanti.tastoCercaAspetto}
        </button>
        <button
          onClick={esportaCsv}
          className={stilePulsanti.buttonClassesWhite}
          disabled={loading || rows.length === 0}
        >
          Esporta CSV con dati grane
        </button>
      </div>

      <div className="gray tc mb3">
        Range applicato (Data_partenza): {rangeApplicato.dataStart} →{" "}
        {rangeApplicato.dataEnd}
      </div>

      {error && <div className="dark-red tc mb3">{error}</div>}

      <DataTable
        data={rows}
        columns={columns}
        progressPending={loading}
        noDataComponent={
          <div className="pa4 tc">Nessuna spedizione con grana nel range.</div>
        }
        pagination
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        paginationPerPage={10}
        paginationComponentOptions={{
          rowsPerPageText: "",
          rangeSeparatorText: "di",
          noRowsPerPage: false,
          selectAllRowsItem: false,
          selectAllRowsItemText: "Seleziona tutti",
        }}
      />
    </div>
  );
}

export default StatDatiGraneCorrieri;
